/**
 * Composes keyboard, gamepad, and touch into one InputFrame per tick. Buttons
 * OR together, axes take the larger magnitude, so every device can be used at
 * once. Nothing is smoothed: the sim sees exactly what the devices reported
 * at the start of the tick.
 */

import type { InputFrame, Tuning } from "@lache/sim";
import { radialDeadzone, reachCurve, triggerDeadzone, type Vec2 } from "./deadzone";
import { GamepadReader } from "./gamepad";
import { KeyboardReader } from "./keyboard";
import { padScheme } from "./schemes";
import { createTouchFrame, TouchReader } from "./touch";

export interface InputConditioning {
  stickDeadzone: number;
  triggerDeadzone: number;
  reachCurve: number;
  /** 1: the left stick (or d-pad, or W/S) up jumps and down tucks. */
  stickActions: number;
  stickActionThreshold: number;
  /** Index into PAD_SCHEMES: what the left trigger, the bumpers, and the right stick's vertical axis do. */
  padScheme: number;
  /** Trigger value past which a trigger acting as a button (the left trigger as Tuck) counts as pressed. */
  triggerActionThreshold: number;
  /** Left stick magnitude at or above which a diagonal push counts as a full run; 0 disables. */
  stickRunAssist: number;
  /** Minimum horizontal deflection for the run assist. */
  stickRunAssistMinX: number;
  /** On a bar, a pad stick jump counts only when the stick crosses the threshold within this angle of straight up, degrees. */
  barStickJumpDeg: number;
  /** 1 while the character holds a bar; the app sets it from the simulation before each sample. */
  onBar: number;
  /** 1 while the body is in the air and on no bar or ledge (the app sets it each tick), for the touch dive. */
  inAir: number;
  /** Read-only swing context for touch assistance; it only emits ordinary input frames. */
  onLadder: number;
  barTheta: number;
  barOmega: number;
  /** 1 during the flight after a bar release, before landing. */
  betweenBars: number;
}

function pickAxis(a: number, b: number): number {
  return Math.abs(a) >= Math.abs(b) ? a : b;
}

/**
 * Run assist: a stick pushed to the rim on a diagonal (a stick jump or tuck from a run) is a full run
 * in that direction, so the vertical push does not bleed speed through moveX.
 */
function assistRun(x: number, y: number, cond: InputConditioning): number {
  if (cond.stickRunAssist <= 0) return x;
  const m = Math.sqrt(x * x + y * y);
  return m >= cond.stickRunAssist && Math.abs(x) >= cond.stickRunAssistMinX ? (x < 0 ? -1 : 1) : x;
}

/** Whether a push (x, v) with v the vertical magnitude lies within `deg` of vertical; 90 or more is any push. */
function withinVertical(x: number, v: number, deg: number): boolean {
  return deg >= 90 || Math.abs(x) <= v * Math.tan((deg * Math.PI) / 180);
}

/**
 * Up jumps and down tucks on one stick (the pad's left stick with its d-pad, or the touch stick).
 * Held state only; the sim derives the press edge, so pushing up jumps once and holding it keeps the
 * jump long. On a bar the stick's horizontal axis pumps, so a push that reaches the threshold off to
 * the side (a sweep across the top of the gate) does not jump; a push within barStickJumpDeg of
 * straight up does, and stays a jump while held.
 *
 * `gateDeg` below 90 also keeps a diagonal from jumping or tucking anywhere: the touch stick uses it,
 * because a thumb that swings to run on glass drifts up or down with nothing to feel. A push that
 * comes within the gate while held past the threshold still fires, and once firing it holds through
 * any direction until the push falls back under the threshold.
 */
class StickActions {
  private up = false;
  jump = false;
  tuck = false;

  update(x: number, y: number, cond: InputConditioning, gateDeg = 90): void {
    const on = cond.stickActions > 0;
    const up = on && y >= cond.stickActionThreshold;
    const down = on && y <= -cond.stickActionThreshold;
    if (!up) this.jump = false;
    else if (cond.onBar !== 0) {
      if (!this.up) this.jump = withinVertical(x, y, Math.min(gateDeg, cond.barStickJumpDeg));
    } else if (!this.jump && (!this.up || gateDeg < 90)) this.jump = withinVertical(x, y, gateDeg);
    this.up = up;
    if (!down) this.tuck = false;
    else if (!this.tuck) this.tuck = withinVertical(x, -y, gateDeg);
  }
}

export class InputSource {
  readonly keyboard: KeyboardReader;
  readonly gamepad: GamepadReader;
  readonly touch: TouchReader;
  private readonly stick: Vec2 = { x: 0, y: 0 };
  private readonly padActions = new StickActions();
  private readonly touchActions = new StickActions();
  /** What the active pad scheme lets the pad's left stick do on its own. */
  private padStickUp = true;
  private padStickDown = true;
  private readonly touchFrame = createTouchFrame();
  private readonly tilt: Vec2 = { x: 0, y: 0 };
  /** Time of the most recent button edge from any device (performance.now()). */
  lastEdgeTime = 0;
  /**
   * Whether the keyboard feeds this source. A split-screen seat holds either the keyboard or a pad,
   * so the other seats' sources share the one keyboard reader with this switched off.
   */
  keyboardEnabled = true;

  constructor(keyboard = new KeyboardReader(), gamepad = new GamepadReader(), touch = new TouchReader()) {
    this.keyboard = keyboard;
    this.gamepad = gamepad;
    this.touch = touch;
  }

  /** Poll devices and write the frame. `now` is performance.now() at the poll. */
  sample(out: InputFrame, cond: InputConditioning, now: number): void {
    const kb = this.keyboard;
    const useKb = this.keyboardEnabled;
    const hasPad = this.gamepad.poll(now);
    const pad = this.gamepad.frame;
    const touch = this.touchFrame;
    this.touch.setBarContext(cond.onBar !== 0, cond.onLadder !== 0);
    this.touch.read(touch);

    let moveX = useKb ? kb.axis("moveLeft", "moveRight") : 0;
    let moveY = useKb ? kb.axis("moveDown", "moveUp") : 0;
    const keyY = moveY;
    let padX = 0;
    let padY = 0;
    let tiltX = useKb ? kb.axis("tiltBack", "tiltForward") : 0;
    let tiltY = 0;
    let reach = useKb && kb.held("reach") ? 1 : 0;
    let pull = useKb && kb.held("pull") ? 1 : 0;
    let jump = useKb && kb.held("jump");
    let tuck = useKb && kb.held("tuck");
    let restart = useKb && kb.held("restart");
    let pause = useKb && kb.held("pause");

    if (hasPad) {
      // Scheme: the left trigger, the bumpers, the right stick's vertical axis, and whether the left
      // stick's own up and down still jump and tuck.
      const scheme = padScheme(cond.padScheme);
      radialDeadzone(pad.lx, pad.ly, cond.stickDeadzone, this.stick);
      radialDeadzone(pad.rx, pad.ry, cond.stickDeadzone, this.tilt);
      const lt = triggerDeadzone(pad.lt, cond.triggerDeadzone);
      // Rotate: while the left trigger is held the left stick is the torso, not the legs, so its
      // horizontal axis tilts, locomotion waits, and its up and down do nothing.
      const rotating = scheme.lt === "rotate" && lt >= cond.triggerActionThreshold;
      if (rotating) {
        tiltX = pickAxis(tiltX, this.stick.x);
        padX = pad.dpadX;
        padY = pad.dpadY;
        moveX = pickAxis(moveX, pad.dpadX);
      } else {
        const sx = assistRun(this.stick.x, this.stick.y, cond);
        moveX = pickAxis(moveX, pickAxis(sx, pad.dpadX));
        padX = pickAxis(this.stick.x, pad.dpadX);
        padY = pickAxis(this.stick.y, pad.dpadY);
      }
      moveY = pickAxis(moveY, padY);
      tiltX = pickAxis(tiltX, this.tilt.x);
      tiltY = pickAxis(tiltY, this.tilt.y);
      reach = Math.max(reach, reachCurve(triggerDeadzone(pad.reach, cond.triggerDeadzone), cond.reachCurve));
      jump = jump || pad.jump;
      tuck = tuck || pad.tuck;
      restart = restart || pad.restart;
      pause = pause || pad.pause;

      pull = Math.max(pull, pad.pull);
      if (scheme.lt === "pull") pull = Math.max(pull, lt);
      else if (scheme.lt === "tuck" && lt >= cond.triggerActionThreshold) tuck = true;
      if (pad.lb) {
        if (scheme.lb === "jump") jump = true;
        else if (scheme.lb === "tuck") tuck = true;
        else if (scheme.lb === "pull") pull = 1;
      }
      if (pad.rb) {
        if (scheme.rb === "jump") jump = true;
        else if (scheme.rb === "tuck") tuck = true;
        else if (scheme.rb === "pull") pull = 1;
      }
      const ty = this.tilt.y;
      if (scheme.rsUp === "reach" && ty >= cond.stickActionThreshold) reach = Math.max(reach, ty);
      if (ty <= -cond.stickActionThreshold) {
        if (scheme.rsDown === "tuck") tuck = true;
        else if (scheme.rsDown === "pull") pull = Math.max(pull, -ty);
      }
      this.padStickUp = scheme.stickUp === "jump";
      this.padStickDown = scheme.stickDown === "tuck";
    }

    // Touch: the on-screen stick is a left stick, the lean a right stick's x, GRAB the reach at full press.
    if (touch.active) {
      moveX = pickAxis(moveX, assistRun(touch.moveX, touch.moveY, cond));
      moveY = pickAxis(moveY, touch.moveY);
      let touchTilt = touch.lean;
      // While the left thumb steers a bar, it owns the pump. Moving the grip thumb toward
      // RELEASE must not add a rightward pump or cancel a leftward one. With a neutral stick,
      // right-thumb pumping still works; flight lean and salmon-ladder controls are unchanged.
      if (
        this.touch.config.lacheAssist &&
        cond.onBar === 1 &&
        cond.onLadder === 0 &&
        touch.reach > 0 &&
        Math.abs(touch.moveX) >= this.touch.config.pumpIntentThreshold
      )
        touchTilt = 0;
      // Blend through thumb drift instead of switching the dive off just beyond the deadzone.
      // With lache help the first jump stays upright; a dive is useful during a bar-to-bar flight.
      if (
        this.touch.config.autoDive &&
        cond.inAir === 1 &&
        touch.reach > 0 &&
        (!this.touch.config.lacheAssist || cond.betweenBars === 1)
      )
        touchTilt += touch.moveX * (1 - Math.abs(touch.lean));
      tiltX = pickAxis(tiltX, touchTilt);
      reach = Math.max(reach, touch.reach);
      jump = jump || touch.jump;
      tuck = tuck || touch.tuck;
      restart = restart || touch.restart;
      pause = pause || touch.pause;
    }

    // Stick actions: up is Jump, down is Tuck, on top of the buttons. W and the up arrow always jump,
    // S and the down arrow always tuck.
    // The pad scheme decides whether its stick still jumps and tucks (the owner's 2026-09-18 scheme
    // keeps the jump and leaves the crouch to the right face button); the touch stick keeps both,
    // since its own layout is the phone's (docs/mobile.md).
    this.padActions.update(padX, padY, cond);
    this.touchActions.update(touch.moveX, touch.moveY, cond, this.touch.config.stickActionDeg);
    if (cond.stickActions > 0) {
      const padJump = this.padStickUp && this.padActions.jump;
      const padTuck = this.padStickDown && this.padActions.tuck;
      if (keyY >= cond.stickActionThreshold || padJump || this.touchActions.jump) jump = true;
      if (keyY <= -cond.stickActionThreshold || padTuck || this.touchActions.tuck) tuck = true;
    }
    out.moveX = moveX;
    out.moveY = moveY;
    out.tiltX = tiltX;
    out.tiltY = tiltY;
    out.reach = reach;
    out.pull = pull;
    out.jump = jump;
    out.tuck = tuck;
    out.restart = restart;
    out.pause = pause;
    this.lastEdgeTime = Math.max(
      useKb ? kb.lastEdgeTime : 0,
      this.gamepad.lastEdgeTime,
      this.touch.lastEdgeTime,
    );
  }
}

export function conditioningFromTuning(t: Tuning): InputConditioning {
  return {
    stickDeadzone: t.stickDeadzone,
    triggerDeadzone: t.triggerDeadzone,
    reachCurve: t.reachCurve,
    stickActions: t.stickActions,
    stickActionThreshold: t.stickActionThreshold,
    padScheme: t.padScheme,
    triggerActionThreshold: t.triggerActionThreshold,
    stickRunAssist: t.stickRunAssist,
    stickRunAssistMinX: t.stickRunAssistMinX,
    barStickJumpDeg: t.barStickJumpDeg,
    onBar: 0,
    inAir: 0,
    onLadder: 0,
    barTheta: 0,
    barOmega: 0,
    betweenBars: 0,
  };
}
